import React, { useState, useEffect } from "react";
import { Form, Button, Container, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { useSelector } from "react-redux";

const API_DOMAIN = "https://estbn.com/EstbnNew/public/api/";
const PROPERTIES_ENDPOINT = API_DOMAIN + "portal/properties";
// const AUTH_TOKEN = "1|uXWWHVAxUbLR94IuQ07nZUT5306vlnjfGFo4AUoR16bcf002";

const AddProperties = ({
  categoryIdFromProps,
  onClose,
  onSuccess,
  initialPropertyData,
}) => {
  const { user } = useSelector((state) => state.auth);

  const AUTH_TOKEN = user?.token;

  const [propertyName, setPropertyName] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [isFilterable, setIsFilterable] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [propertyValues, setPropertyValues] = useState([""]);

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (initialPropertyData) {
      setPropertyName(initialPropertyData.name || "");
      setPropertyType(initialPropertyData.type || "");
      setSortOrder(
        initialPropertyData.sortOrder !== undefined
          ? String(initialPropertyData.sortOrder)
          : ""
      );
      setIsFilterable(!!initialPropertyData.isFilterable);
      setIsRequired(!!initialPropertyData.isRequired);
      if (
        initialPropertyData.type === "select" &&
        Array.isArray(initialPropertyData.values) &&
        initialPropertyData.values.length > 0
      ) {
        setPropertyValues(initialPropertyData.values);
      } else {
        setPropertyValues([""]);
      }
    } else {
      setPropertyName("");
      setPropertyType("");
      setSortOrder("");
      setIsFilterable(false);
      setIsRequired(false);
      setPropertyValues([""]);
    }
    setApiError("");
    setFormErrors({});
  }, [initialPropertyData]);

  const validateForm = () => {
    const errors = {};
    if (!propertyName.trim()) {
      errors.propertyName = "Property name is required.";
    }
    if (!propertyType.trim()) {
      errors.propertyType = "Property type is required.";
    }
    if (sortOrder === "") {
      errors.sortOrder = "Sort order is required.";
    }
    if (!categoryIdFromProps && !initialPropertyData?.categoryId) {
      errors.general = "Category ID is missing. Cannot create/update property.";
    }
    if (propertyType === "select") {
      const hasEmptyValue = propertyValues.some((value) => !value.trim());
      if (propertyValues.length === 0 || hasEmptyValue) {
        errors.propertyValues =
          "Select property must have at least one non-empty value.";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleValueChange = (index, event) => {
    const newValues = [...propertyValues];
    newValues[index] = event.target.value;
    setPropertyValues(newValues);
    if (formErrors.propertyValues) {
      setFormErrors({ ...formErrors, propertyValues: "" });
    }
  };

  const handleAddValue = () => {
    setPropertyValues([...propertyValues, ""]);
  };

  const handleRemoveValue = (index) => {
    const newValues = propertyValues.filter((_, i) => i !== index);
    setPropertyValues(newValues.length > 0 ? newValues : [""]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setApiError("");
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append("name", propertyName);
    formData.append("type", propertyType);
    formData.append("sort_order", parseInt(sortOrder));
    formData.append("is_filterable", isFilterable ? "1" : "0");
    formData.append("is_required", isRequired ? "1" : "0");

    formData.append(
      "category_id",
      categoryIdFromProps || initialPropertyData?.categoryId
    );

    if (propertyType === "select") {
      propertyValues.forEach((value, index) => {
        if (value.trim()) {
          formData.append(`values[${index}]`, value.trim());
        }
      });
    }

    console.log("Submitting Property Data (FormData):");
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }

    const isUpdate = initialPropertyData && initialPropertyData.id;
    const url = isUpdate
      ? `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}`
      : PROPERTIES_ENDPOINT;
    const method = "POST";

    if (isUpdate) {
      formData.append("_method", "PUT");
    }

    try {
      const response = await axios({
        method: method,
        url: url,
        data: formData,
        headers: {
          Authorization: `Bearer ${AUTH_TOKEN}`,
        },
      });

      console.log("API Response:", response.data);

      if (response.data && response.data.success) {
        if (onSuccess) {
          onSuccess(response.data.data.property);
        }
      } else {
        setApiError(
          response.data.message ||
            `Failed to ${isUpdate ? "update" : "create"} property.`
        );
      }
    } catch (err) {
      console.error("API Error:", err.response || err.message || err);
      let errorMessage = `An error occurred while ${
        isUpdate ? "updating" : "creating"
      } the property.`;
      if (err.response && err.response.data) {
        errorMessage = err.response.data.message || errorMessage;
        if (err.response.data.errors) {
          const backendErrors = err.response.data.errors;
          const newFormErrors = { ...formErrors };
          for (const key in backendErrors) {
            if (key === "name")
              newFormErrors.propertyName = backendErrors[key][0];
            if (key === "type")
              newFormErrors.propertyType = backendErrors[key][0];
            if (key === "sort_order")
              newFormErrors.sortOrder = backendErrors[key][0];
            if (key === "values" || key.startsWith("values."))
              newFormErrors.propertyValues = backendErrors[key][0];
          }
          setFormErrors(newFormErrors);
          if (Object.keys(newFormErrors).length === 0) {
            setApiError(errorMessage);
          }
        } else {
          setApiError(errorMessage);
        }
      } else {
        setApiError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (setter, fieldName) => (e) => {
    setter(e.target.value);
    if (formErrors[fieldName]) {
      setFormErrors({ ...formErrors, [fieldName]: "" });
    }
    if (fieldName === "propertyType") {
      if (e.target.value !== "select") {
        setPropertyValues([""]);
      }
      if (e.target.value === "select" && propertyValues.length === 0) {
        setPropertyValues([""]);
      }
    }
  };

  const handleCheckboxChange = (setter) => (e) => {
    setter(e.target.checked);
  };

  const modalTitle = initialPropertyData
    ? "Update Property"
    : "Add New Property";
  const submitButtonText = initialPropertyData ? "Update" : "Save";

  return (
    <Container className="px-2" style={{ maxWidth: "600px" }}>
      {apiError && (
        <Alert variant="danger" className="mt-3">
          {apiError}
        </Alert>
      )}

      <Form noValidate onSubmit={handleSubmit}>
        <Form.Group className="mb-2" controlId="formPropertyName">
          <Form.Label>Property Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter property name"
            value={propertyName}
            onChange={handleInputChange(setPropertyName, "propertyName")}
            isInvalid={!!formErrors.propertyName}
            disabled={isLoading}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors.propertyName}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-2" controlId="formPropertyType">
          <Form.Label>Type</Form.Label>
          <Form.Control
            as="select"
            value={propertyType}
            onChange={handleInputChange(setPropertyType, "propertyType")}
            isInvalid={!!formErrors.propertyType}
            disabled={isLoading}
          >
            <option value="">Select property type</option>
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="select">Select</option>
            <option value="boolean">Boolean</option>
          </Form.Control>
          <Form.Control.Feedback type="invalid">
            {formErrors.propertyType}
          </Form.Control.Feedback>
        </Form.Group>

        {propertyType === "select" && (
          <Form.Group className="mb-2" controlId="formPropertyValues">
            <Form.Label>Values (for Select Type)</Form.Label>
            {propertyValues.map((value, index) => (
              <div key={index} className="d-flex mb-2">
                <Form.Control
                  type="text"
                  placeholder={`Value ${index + 1}`}
                  value={value}
                  onChange={(e) => handleValueChange(index, e)}
                  isInvalid={!!formErrors.propertyValues && index === 0}
                  disabled={isLoading}
                />
                {propertyValues.length > 1 && (
                  <Button
                    variant="outline-danger"
                    className="ms-2"
                    onClick={() => handleRemoveValue(index)}
                    disabled={isLoading}
                  >
                    -
                  </Button>
                )}
              </div>
            ))}
            <Button
              variant="outline-primary"
              onClick={handleAddValue}
              disabled={isLoading}
            >
              + Add Value
            </Button>
            <Form.Control.Feedback
              type="invalid"
              className={formErrors.propertyValues ? "d-block" : ""}
            >
              {formErrors.propertyValues}
            </Form.Control.Feedback>
          </Form.Group>
        )}

        <Form.Group className="mb-2" controlId="formSortOrder">
          <Form.Label>Sort Order</Form.Label>
          <Form.Control
            as="select"
            value={sortOrder}
            onChange={handleInputChange(setSortOrder, "sortOrder")}
            isInvalid={!!formErrors.sortOrder}
            disabled={isLoading}
          >
            <option value="">Select sort order</option>
            {[...Array(11)].map(
              (
                _,
                index // 0 to 10
              ) => (
                <option key={index} value={index}>
                  {index}
                </option>
              )
            )}
          </Form.Control>
          <Form.Control.Feedback type="invalid">
            {formErrors.sortOrder}
          </Form.Control.Feedback>
        </Form.Group>

        <div className="d-flex mb-0">
          <Form.Check
            type="checkbox"
            id="isFilterable"
            label="Is Filterable"
            checked={isFilterable}
            onChange={handleCheckboxChange(setIsFilterable)}
            className="me-4"
            disabled={isLoading}
          />
          <Form.Check
            type="checkbox"
            id="isRequired"
            label="Is Required"
            checked={isRequired}
            onChange={handleCheckboxChange(setIsRequired)}
            disabled={isLoading}
          />
        </div>

        <div className="d-flex justify-content-center gap-3 mt-3">
          <Button
            className="w-25 h-100"
            variant="outline-secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="dark"
            type="submit"
            className="w-25 h-100"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              submitButtonText
            )}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default AddProperties;
